import { CodeArticleVersion } from '@livv/models';
import { Grid, Typography } from '@mui/material';
import { Diff } from 'diff-match-patch';
import parse from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import MonoDiff from './MonoDiff';
import PlaceHolderInstructions from './PlaceHolderInstructions';
import { accentColors } from '@styles/theme';
import computeDiff, { groupParagraphs, DIFF_MODE } from '@utils/diff';
import { stripHtmlTags } from '@utils/parsing';

interface VersionsDiffProps {
    versionsSelected: CodeArticleVersion[];
}

const NEW_LINE = `
`;

const preprocess = (texteHtml: string): string =>
    stripHtmlTags(texteHtml.replace(/<\/p>/g, NEW_LINE).replace(/<br\s?\/?>/g, NEW_LINE)).trim();

const VersionsDiff: FC<VersionsDiffProps> = ({ versionsSelected }) => {
    const { t } = useTranslation('codeArticle');
    const twoVersionsSelected = versionsSelected.length === 2;
    const [oldValue, newValue] = [...versionsSelected].sort(
        ({ dateDebut: startDate1 }, { dateDebut: startDate2 }) =>
            startDate1.seconds - startDate2.seconds,
    );

    const paragraphDiffs = twoVersionsSelected
        ? groupParagraphs(
              computeDiff(preprocess(oldValue.texteHtml), preprocess(newValue.texteHtml)),
          )
        : ([[]] as Diff[][]);

    return (
        <Grid direction="row" sx={{ mt: 1 }} container>
            {twoVersionsSelected && oldValue ? (
                // left block : original version or deletions
                <Grid sx={{ pr: 3 }} xs={6} item>
                    <Typography sx={{ color: accentColors[5], fontWeight: 'medium' }}>
                        {t('previousVersion')}
                    </Typography>
                    <MonoDiff
                        diffMode={DIFF_MODE.DELETE}
                        paragraphsDiffs={paragraphDiffs}
                        version={oldValue}
                    />
                </Grid>
            ) : (
                <Grid xs={6} item>
                    <Typography component="div">
                        {parse(DOMPurify.sanitize(versionsSelected[0].texteHtml))}
                    </Typography>
                </Grid>
            )}
            <Grid
                sx={{
                    borderColor: 'grey.600',
                    borderLeft: 1,
                    pl: 3,
                }}
                xs={6}
                item
            >
                {twoVersionsSelected && newValue ? (
                    // right block : placeholder or insertions
                    <>
                        <Typography sx={{ color: 'info.main', fontWeight: 'medium' }}>
                            {t('version')}
                        </Typography>
                        <MonoDiff
                            diffMode={DIFF_MODE.INSERT}
                            paragraphsDiffs={paragraphDiffs}
                            version={newValue}
                        />
                    </>
                ) : (
                    <PlaceHolderInstructions />
                )}
            </Grid>
        </Grid>
    );
};

export default VersionsDiff;
